import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Anchor,
  Banknote,
  BarChart3,
  BookOpen,
  Building2,
  CalendarCheck,
  ChevronRight,
  FileText,
  Inbox,
  Landmark,
  LayoutDashboard,
  LogOut,
  ReceiptText,
  Settings,
  Ship,
  Wallet,
} from "lucide-react";
import { logout } from "@/api/client";
import { fetchHealth } from "@/api/health";
import { useMailStatus } from "@/api/mail";
import { useMe } from "@/api/users";
import { APP_VERSION, idleTimeoutMinutes, isOutdated, useServerVersion } from "@/api/version";
import { IdleSignOut } from "@/components/layout/IdleSignOut";
import { useAndroidBackButton } from "@/lib/android-back";
import { isNativeShell, shellName } from "@/lib/platform";
import { cn } from "@/lib/utils";

export type NavGroup = "Work" | "Money" | "Accounting" | "Records";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** The sidebar section; Settings has none and sits at the foot of the sidebar. */
  group?: NavGroup;
  end?: boolean;
  /** Shown in the phone bottom bar. */
  mobile?: boolean;
  /** Not built yet: shown disabled in the sidebar only. */
  soon?: boolean;
  /** Only once the server has a mailbox to read. */
  whenConfigured?: true;
};

const NAV_GROUPS: NavGroup[] = ["Work", "Money", "Accounting", "Records"];

export const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, group: "Work", end: true, mobile: true },
  { to: "/operations", label: "Operations", icon: Ship, group: "Work", mobile: true },
  { to: "/mail", label: "Mail", icon: Inbox, group: "Work", whenConfigured: true },
  { to: "/invoices", label: "Invoices", icon: FileText, group: "Work", mobile: true },
  // Phones reach payments from each invoice and the dashboard.
  { to: "/payments", label: "Payments", icon: Wallet, group: "Money" },
  { to: "/receipts", label: "Receipts", icon: ReceiptText, group: "Money" },
  { to: "/expenses", label: "Expenses", icon: Banknote, group: "Money" },
  { to: "/money", label: "Money", icon: Landmark, group: "Money" },
  { to: "/reports", label: "Reports", icon: BarChart3, group: "Accounting" },
  { to: "/books", label: "Books", icon: BookOpen, group: "Accounting" },
  { to: "/compliance", label: "Compliance", icon: CalendarCheck, group: "Accounting" },
  { to: "/clients", label: "Clients", icon: Building2, group: "Records", mobile: true },
  { to: "/vessels", label: "Vessels", icon: Anchor, group: "Records" },
  { to: "/settings", label: "Settings", icon: Settings, mobile: true },
];

/** The navigation item a path belongs to: "/invoices/42" is under Invoices. */
export function navItemFor(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}

function ApiStatus() {
  const health = useQuery({ queryKey: ["health"], queryFn: () => fetchHealth(), retry: false });
  const label = health.isPending ? "Checking API…" : health.isError ? "API offline" : "API online";
  const tone = health.isPending
    ? "bg-muted-foreground"
    : health.isError
      ? "bg-destructive"
      : "bg-success";
  return (
    <span
      role="status"
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground"
    >
      <span className={cn("h-2 w-2 rounded-full", tone)} aria-hidden />
      <span className="sr-only sm:not-sr-only">{label}</span>
    </span>
  );
}

/**
 * Sideloaded apps do not update themselves, so the server states the oldest build it accepts
 * and an older one says so on every page.
 */
function UpdateBanner() {
  const version = useServerVersion();
  const minimum = version.data?.min_client_version;
  if (minimum === undefined || !isOutdated(APP_VERSION, minimum)) return null;
  return (
    <div role="alert" className="border-b border-warning/40 bg-warning/10 px-4 py-2 text-sm md:px-8">
      <span className="font-semibold">Update needed.</span>{" "}
      <span className="text-foreground/80">
        {isNativeShell()
          ? `This build (${APP_VERSION}) is older than the server expects (${minimum}). Install the newest build of ${shellName()} to keep working.`
          : `This page (${APP_VERSION}) is older than the server expects (${minimum}). Reload to pick up the newest version.`}
      </span>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function SidebarLink({ item }: { item: NavItem }) {
  if (item.soon) {
    return (
      <span
        aria-disabled="true"
        className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-muted"
      >
        <item.icon className="h-[18px] w-[18px]" aria-hidden />
        {item.label}
        <span className="ml-auto rounded bg-white/10 px-1.5 py-0.5 text-[10px]">Soon</span>
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-md px-3 py-[7px] text-sm font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold",
          isActive
            ? "bg-sidebar-active text-white before:absolute before:inset-y-2 before:-left-3 before:w-[3px] before:rounded-r before:bg-gold"
            : "text-sidebar-foreground hover:bg-sidebar-hover hover:text-white",
        )
      }
    >
      <item.icon className="h-[18px] w-[18px] shrink-0 opacity-90" aria-hidden />
      {item.label}
    </NavLink>
  );
}

function Sidebar({ items }: { items: NavItem[] }) {
  const ungrouped = items.filter((item) => !item.group);
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-sidebar text-sidebar-foreground md:flex">
      <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/10 px-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white p-1">
          <img src="/pb-logo.png" alt="" className="h-auto w-full" />
        </span>
        <span className="min-w-0 font-display leading-none">
          <span className="block truncate text-[15px] font-bold tracking-wide text-white">
            Pelican Blue Ocean
          </span>
          <span className="mt-1 block text-xs font-medium tracking-wide text-gold-light">
            Invoicing
          </span>
        </span>
      </div>
      <nav
        aria-label="Main"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4 [scrollbar-color:hsl(var(--sidebar-active))_transparent] [scrollbar-width:thin]"
      >
        <div className="flex flex-col gap-4">
          {NAV_GROUPS.map((group) => {
            const links = items.filter((item) => item.group === group);
            if (links.length === 0) return null;
            return (
              <div key={group} role="group" aria-labelledby={`nav-${group}`}>
                <p id={`nav-${group}`} className="px-3 pb-1 text-xs font-medium text-sidebar-muted">
                  {group}
                </p>
                <div className="flex flex-col gap-0.5">
                  {links.map((item) => (
                    <SidebarLink key={item.to} item={item} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-auto flex flex-col gap-0.5 border-t border-white/10 pt-3">
          {ungrouped.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </div>
      </nav>
    </aside>
  );
}

function Breadcrumb() {
  const { pathname } = useLocation();
  const item = navItemFor(pathname);
  if (!item) return null;
  return (
    <p className="hidden min-w-0 items-center gap-1.5 text-sm md:flex">
      {item.group && item.group !== item.label && (
        <>
          <span className="text-muted-foreground">{item.group}</span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </>
      )}
      <span className="truncate font-medium text-foreground">{item.label}</span>
    </p>
  );
}

/** Signed-in shell: sidebar on desktop, top bar, and a bottom tab bar on phones. */
export function AppLayout() {
  const me = useMe();
  const mail = useMailStatus();
  const version = useServerVersion();
  useAndroidBackButton();
  const items = NAV_ITEMS.filter((item) => !item.whenConfigured || mail.data?.configured);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>

      <Sidebar items={items} />

      <div className="flex min-h-screen flex-col md:pl-60">
        <header className="sticky top-0 z-30 border-b bg-card/95 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-card/85">
          <div className="flex h-14 items-center justify-between gap-3 px-4 md:px-8">
            <img
              src="/pbo_logo.png"
              alt="Pelican Blue Ocean"
              className="h-8 w-auto shrink-0 md:hidden"
            />
            <Breadcrumb />
            <div className="ml-auto flex min-w-0 items-center gap-2 md:gap-3">
              <ApiStatus />
              {me.data && (
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                    aria-hidden
                  >
                    {initials(me.data.full_name)}
                  </span>
                  <span className="hidden max-w-[12rem] truncate text-sm font-medium sm:inline">
                    {me.data.full_name}
                  </span>
                </span>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                aria-label="Sign out"
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-input bg-card px-2.5 text-xs font-semibold text-foreground transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        <UpdateBanner />
        <IdleSignOut minutes={idleTimeoutMinutes(version.data)} />

        <main
          id="main"
          tabIndex={-1}
          className="mx-auto w-full min-w-0 max-w-[1440px] flex-1 px-4 pb-28 pt-6 outline-none md:px-8 md:pb-12 md:pt-8"
        >
          <Outlet />
        </main>
      </div>

      <nav
        aria-label="Main (mobile)"
        className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t bg-card/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_12px_-6px_rgb(10_31_77/0.12)] backdrop-blur md:hidden"
      >
        {items
          .filter((item) => item.mobile)
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "relative flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  isActive
                    ? "text-primary before:absolute before:inset-x-5 before:top-0 before:h-0.5 before:rounded-b before:bg-primary"
                    : "text-muted-foreground",
                )
              }
            >
              <item.icon className="h-5 w-5" aria-hidden />
              {item.label}
            </NavLink>
          ))}
      </nav>
    </div>
  );
}
